import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Container, Card, CardBody, Row, Col, Spinner, Label, Input, Button } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SearchableSelect from "../../../Components/Common/SearchableSelect";
import FilterCard from "../../../Components/Common/FilterCard";
import { useAlertesQuery } from "../../../hooks/queries/useRoadAlertQueries";
import { useFilterData } from "../Dashboard/filter-hook";
import { useAuthStore } from "../../../stores/useAuthStore";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AlerteDetail } from "../AlerteDetail/types";
import AlerteDetailContent from "../AlerteDetail/AlerteDetailContent";
import { showModal } from "../../../hooks/useModal";
import { useLayoutStore } from "../../../stores/useLayoutStore";

// Fix pour les icônes Leaflet par défaut
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Composant pour ajuster la vue de la carte selon les marqueurs
const MapBounds = ({ alertes }: { alertes: AlerteDetail[] }) => {
  const map = useMap();
  
  useEffect(() => {
    const validAlertes = alertes.filter(
      (a) => a.latitude && a.longitude && !isNaN(Number(a.latitude)) && !isNaN(Number(a.longitude))
    );
    
    if (validAlertes.length > 0) {
      const bounds = L.latLngBounds(
        validAlertes.map((a) => [Number(a.latitude), Number(a.longitude)])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    } else {
      // Vue par défaut (Cameroun approximatif)
      map.setView([7.3697, 12.3547], 6);
    }
  }, [alertes, map]);

  return null;
};

// Couleurs de base pour statut et priorité (utilisées par la carte et les légendes)
// Statuts:
// 1=Non Affectée, 2=Non Demarrée, 3=En Cours (Pause),
// 4=Terminée, 5=Arrêtée, 6=En Cours (Travaux),
// 7=Grand Public, 8=Rejeté
// Priorités:
// 3=TRES URGENT, 2=URGENT, 1=NORMAL, 0=NON DEFINI
const getStatutColor = (statutId?: number): string => {
  switch (statutId) {
    case 1:
      return "orange";
    case 2:
      return "yellow";
    case 3:
      return "violet";
    case 4:
      return "grey";
    case 5:
      return "black";
    case 6:
      return "green";
    case 7:
      return "blue";
    case 8:
      return "red";
    default:
      return "blue";
  }
};

const getPrioriteColor = (prioriteId?: number): string => {
  if (prioriteId === 3) return "red"; // TRES URGENT
  if (prioriteId === 2) return "yellow"; // URGENT (proche orange)
  if (prioriteId === 1) return "blue"; // NORMAL
  if (prioriteId === 0) return "grey"; // NON DEFINI
  return "blue";
};

const getMarkerIcon = (
  statutId?: number,
  prioriteId?: number,
  mode: "priorite" | "statut" = "priorite"
) => {
  const color =
    mode === "priorite" ? getPrioriteColor(prioriteId) : getStatutColor(statutId);

  return L.icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png`,
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
};

const Cartographie = () => {
  const { user } = useAuthStore();
  const layoutModeType = useLayoutStore((s) => s.layoutModeType);
  const isDarkMode = layoutModeType === "dark";
  const userId = user?.userId || user?._id || user?.id;
  const { options, loading: filtersLoading } = useFilterData();
  const initialLoadDone = useRef(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Calcul des dates : premier jour de l'année et dernier jour du mois actuel
  const getFirstDayOfYear = () => {
    const now = new Date();
    return `${now.getFullYear()}-01-01`;
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const year = lastDay.getFullYear();
    const month = String(lastDay.getMonth() + 1).padStart(2, "0");
    const day = String(lastDay.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // État des filtres
  const [filters, setFilters] = useState({
    idCommune: "",
    idPriorite: "",
    idStatutAlerte: "",
    dateDebut: getFirstDayOfYear(),
    dateFin: getLastDayOfMonth(),
  });

  const [searchParams, setSearchParams] = useState<{
    user: string;
    data: {
      idCommune: string;
      creePar: string;
      traitePar: string;
      idStatutAlerte: string;
      idPriorite: string;
      dateCreationParam?: {
        operator: string;
        start: string;
        end: string;
      };
    };
  } | null>(null);

  // Construction du payload : data (filtres) + page + size au même niveau
  const buildPayload = useCallback((filtersData: typeof filters) => {
    const data: {
      idCommune: string;
      creePar: string;
      traitePar: string;
      idStatutAlerte: string;
      idPriorite: string;
      dateCreationParam?: {
        operator: string;
        start: string;
        end: string;
      };
    } = {
      idCommune: filtersData.idCommune || "",
      creePar: "",
      traitePar: "",
      idStatutAlerte: filtersData.idStatutAlerte || "",
      idPriorite: filtersData.idPriorite || "",
    };

    if (filtersData.dateDebut && filtersData.dateFin) {
      data.dateCreationParam = {
        operator: "[]",
        start: filtersData.dateDebut,
        end: filtersData.dateFin,
      };
    }

    return {
      user: userId ? String(userId) : "",
      data,
      page: 0,
      size: 500,
    };
  }, [userId]);

  // Initialisation au chargement
  useEffect(() => {
    if (!filtersLoading && !initialLoadDone.current && userId) {
      initialLoadDone.current = true;
      setSearchParams(buildPayload(filters));
    }
  }, [filtersLoading, userId, buildPayload, filters]);

  // Fonction pour lancer la recherche
  const handleSearch = () => {
    setSearchParams(buildPayload(filters));
  };

  // Requête avec les paramètres de recherche
  const payload = searchParams ?? buildPayload(filters);
  const alertesQuery = useAlertesQuery(payload, {
    enabled: !!userId && !filtersLoading && !!searchParams,
  });

  // Dédupliquer par idAlerte : l'API peut retourner la même alerte plusieurs fois (ex. avec/sans alertesDegradationDtos)
  const alertes = useMemo(() => {
    const items = alertesQuery.data?.items;
    if (!items || !Array.isArray(items)) return [];
    const list = items as AlerteDetail[];
    const byId = new Map<number, AlerteDetail>();
    list.forEach((a) => {
      const id = a.idAlerte != null ? Number(a.idAlerte) : NaN;
      if (Number.isNaN(id)) return;
      const existing = byId.get(id);
      // Garder de préférence l'entrée qui a alertesDegradationDtos (données plus complètes)
      if (!existing || (Array.isArray((a as any).alertesDegradationDtos) && (a as any).alertesDegradationDtos?.length > 0)) {
        byId.set(id, a);
      }
    });
    return Array.from(byId.values());
  }, [alertesQuery.data]);

  // Filtrer les alertes avec coordonnées valides
  const alertesAvecCoordonnees = useMemo(() => {
    return alertes.filter(
      (alerte) =>
        alerte.latitude &&
        alerte.longitude &&
        !isNaN(Number(alerte.latitude)) &&
        !isNaN(Number(alerte.longitude))
    );
  }, [alertes]);

  // Synthèse pour les légendes : par priorité
  const prioriteSummary = useMemo(() => {
    // Pré-remplir avec toutes les priorités possibles pour toujours les afficher dans la légende
    const base: Record<number, { label: string; count: number }> = {
      3: { label: "TRES URGENT", count: 0 },
      2: { label: "URGENT", count: 0 },
      1: { label: "NORMAL", count: 0 },
      0: { label: "NON DEFINI", count: 0 },
    };

    const summary = { ...base };

    alertesAvecCoordonnees.forEach((alerte) => {
      const prioriteId = alerte.priorite?.idPriorite;
      if (prioriteId == null) return;
      const label =
        alerte.priorite?.libellePriorite ||
        alerte.libellePriorite ||
        base[prioriteId]?.label ||
        `Priorité ${prioriteId}`;

      const current = summary[prioriteId] ?? { label, count: 0 };
      current.count += 1;
      if (!current.label && label) {
        current.label = label;
      }
      summary[prioriteId] = current;
    });

    return summary;
  }, [alertesAvecCoordonnees]);

  const getPrioriteBadgeVariant = useCallback(
    (idPriorite: number, label: string): string => {
      const upper = label.toUpperCase();
      // 3 = TRES URGENT (rouge)
      if (idPriorite === 3 || upper.includes("TRES URGENT")) return "danger";
      // 2 = URGENT (orange)
      if (idPriorite === 2 || upper.includes("URGENT")) return "warning";
      // 1 = NORMAL (vert)
      if (idPriorite === 1 || upper.includes("NORMAL")) return "success";
      // 0 = NON DEFINI (gris)
      return "secondary";
    },
    []
  );

  const getMarkerColorForPriorite = useCallback((idPriorite: number): string => {
    return getPrioriteColor(idPriorite);
  }, []);

  useEffect(() => {
    document.title = "Cartographie | Road Alert AGEROUTE";
  }, []);

  // Intercepter uniquement les événements problématiques (clics) sur le conteneur de la carte
  // pour éviter l'erreur "Permission denied to access property _reactFiber" (événements Leaflet)
  // On ne bloque que les clics, pas les événements nécessaires au zoom et à l'interaction (wheel, mousemove, etc.)
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    // Empêcher uniquement la propagation des clics vers React
    // Les autres événements (wheel pour zoom, mousemove pour drag, etc.) doivent passer
    const stopClickPropagation = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Laisser passer les clics sur les marqueurs et les tooltips
      if (
        target.closest(".leaflet-marker-icon") ||
        target.closest(".leaflet-tooltip")
      ) {
        return;
      }

      // Bloquer uniquement les clics "fond de carte" pour éviter les erreurs React
      if (target.closest(".leaflet-container") && !target.closest(".leaflet-popup-content")) {
        e.stopPropagation();
      }
    };

    // Intercepter uniquement les clics en phase de capture
    container.addEventListener('click', stopClickPropagation, true);

    return () => {
      container.removeEventListener('click', stopClickPropagation, true);
    };
  }, [alertesAvecCoordonnees.length]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Cartographie" pageTitle="Road Alert" />
          
          <Row>
            <Col lg="12">
              <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
                <CardBody>
                  <div className="mb-4">
                    <h5 className="card-title mb-0 fw-semibold">Carte des alertes routières</h5>
                    <p className="text-muted mb-0 small">
                      Visualisation géographique des alertes avec filtres
                    </p>
                  </div>

                  {/* Filtres */}
                  <FilterCard title="Filtres de recherche">
                    <Row className="g-3 align-items-end">
                        <Col md={6} lg={3}>
                          <Label className="small text-muted mb-1"><i className="ri-map-pin-line me-1" />Commune</Label>
                          <SearchableSelect
                            options={options.communeOptions}
                            value={filters.idCommune}
                            onChange={(value) =>
                              setFilters((prev) => ({ ...prev, idCommune: String(value || "") }))
                            }
                            placeholder="Sélectionner une commune"
                          />
                        </Col>
                        <Col md={6} lg={3}>
                          <Label className="small text-muted mb-1"><i className="ri-flag-line me-1" />Priorité</Label>
                          <SearchableSelect
                            options={options.prioriteOptions}
                            value={filters.idPriorite}
                            onChange={(value) =>
                              setFilters((prev) => ({ ...prev, idPriorite: String(value || "") }))
                            }
                            placeholder="Sélectionner une priorité"
                          />
                        </Col>
                        <Col md={6} lg={3}>
                          <Label className="small text-muted mb-1"><i className="ri-list-check me-1" />Statut</Label>
                          <SearchableSelect
                            options={options.statutOptions}
                            value={filters.idStatutAlerte}
                            onChange={(value) =>
                              setFilters((prev) => ({ ...prev, idStatutAlerte: String(value || "") }))
                            }
                            placeholder="Sélectionner un statut"
                          />
                        </Col>
                        <Col md={6} lg={3}>
                          <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date début</Label>
                          <Input type="date" className="form-control rounded-3" value={filters.dateDebut} onChange={(e) => setFilters((prev) => ({ ...prev, dateDebut: e.target.value }))} />
                        </Col>
                        <Col md={6} lg={3}>
                          <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date fin</Label>
                          <Input type="date" className="form-control rounded-3" value={filters.dateFin} onChange={(e) => setFilters((prev) => ({ ...prev, dateFin: e.target.value }))} />
                        </Col>
                        <Col md={6} lg={3} className="d-flex align-items-end">
                          <Button
                            className="w-100 btn-ageroute-orange rounded-pill shadow-sm"
                            onClick={handleSearch}
                            disabled={filtersLoading || alertesQuery.isLoading}
                          >
                            <i className="ri-search-line me-1" />
                            Rechercher
                          </Button>
                        </Col>
                      </Row>
                  </FilterCard>

                  {/* Carte */}
                  {alertesQuery.isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Chargement de la carte...</p>
                    </div>
                  ) : alertesAvecCoordonnees.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="ri-map-pin-line fs-48 text-muted mb-3"></i>
                      <h6 className="fs-16">Aucune alerte avec coordonnées</h6>
                      <p className="text-muted">
                        Aucune alerte trouvée avec des coordonnées géographiques valides pour les critères sélectionnés.
                      </p>
                    </div>
                  ) : (
                    <div
                      ref={mapContainerRef}
                      style={{ height: "700px", width: "100%", position: "relative" }}
                    >
                      <MapContainer
                        center={[7.3697, 12.3547]}
                        zoom={6}
                        style={{ height: "100%", width: "100%", zIndex: 1 }}
                        scrollWheelZoom={true}
                        key={alertesAvecCoordonnees.length}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapBounds alertes={alertesAvecCoordonnees} />

                        {alertesAvecCoordonnees.map((alerte) => {
                          const lat = Number(alerte.latitude);
                          const lon = Number(alerte.longitude);
                          const statutId = alerte.statutAlerte?.idStatutAlerte;
                          const prioriteId = alerte.priorite?.idPriorite;
                          return (
                            <Marker
                              key={alerte.idAlerte}
                              position={[lat, lon]}
                              icon={getMarkerIcon(statutId, prioriteId, "priorite")}
                              eventHandlers={{
                                click: () => {
                                  showModal({
                                    title: `Détails de l'alerte - ${alerte.libelleAlerte || "N/A"}`,
                                    size: "xl",
                                    content: (
                                      <AlerteDetailContent
                                        alerteId={alerte.idAlerte}
                                        alerte={alerte}
                                      />
                                    ),
                                    closeOnBackdropClick: false,
                                    haveDefaultActionsButton: false,
                                  });
                                },
                              }}
                            >
                              <Tooltip
                                direction="top"
                                offset={[0, -10]}
                                opacity={1}
                                permanent={false}
                                sticky
                              >
                                <div
                                  className="shadow-sm rounded-3"
                                  style={{
                                    minWidth: 240,
                                    backgroundColor: isDarkMode ? "#1f2933" : "#ffffff",
                                    border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
                                    color: isDarkMode ? "#e5e7eb" : "#111827",
                                  }}
                                >
                                  <div className="p-2">
                                    <h6
                                      className="mb-1 fw-semibold text-truncate"
                                      title={alerte.libelleAlerte || undefined}
                                      style={{ fontSize: "0.9rem" }}
                                    >
                                      {alerte.libelleAlerte || `Alerte #${alerte.idAlerte}`}
                                    </h6>
                                    <div className="mb-2" style={{ fontSize: "0.75rem" }}>
                                      <div
                                        className="d-flex justify-content-between align-items-center mb-1"
                                      >
                                        <span
                                          className={`badge rounded-pill px-2 py-1 me-1 bg-${
                                            alerte.priorite?.idPriorite === 3
                                              ? "danger"
                                              : alerte.priorite?.idPriorite === 2
                                              ? "warning"
                                              : alerte.priorite?.idPriorite === 1
                                              ? "primary"
                                              : "secondary"
                                          }`}
                                        >
                                          {alerte.priorite?.libellePriorite ||
                                            alerte.libellePriorite ||
                                            "Priorité N/D"}
                                        </span>
                                        <span
                                          className="badge rounded-pill px-2 py-1"
                                          style={{
                                            fontWeight: 500,
                                            backgroundColor: getStatutColor(
                                              alerte.statutAlerte?.idStatutAlerte
                                            ),
                                            color: ["yellow", "orange"].includes(
                                              getStatutColor(
                                                alerte.statutAlerte?.idStatutAlerte
                                              )
                                            )
                                              ? "#111827"
                                              : "#ffffff",
                                            border: "1px solid rgba(15,23,42,0.35)",
                                          }}
                                        >
                                          {alerte.statutAlerte?.libelleStatutAlerte ||
                                            alerte.libelleStatutAlerte ||
                                            "Statut N/D"}
                                        </span>
                                      </div>
                                      <div
                                        className={isDarkMode ? "text-gray-300" : "text-muted"}
                                      >
                                        <strong>Commune&nbsp;:</strong>{" "}
                                        {alerte.communes?.libelleCommune ||
                                          alerte.commune?.libelleCommune ||
                                          alerte.libelleCommune ||
                                          "N/A"}
                                      </div>
                                      {alerte.dateCreation && (
                                        <div
                                          className={isDarkMode ? "text-gray-300" : "text-muted"}
                                        >
                                          <strong>Date&nbsp;:</strong>{" "}
                                          {new Date(
                                            alerte.dateCreation
                                          ).toLocaleDateString("fr-FR")}
                                        </div>
                                      )}
                                    </div>
                                    <div
                                      className={isDarkMode ? "text-gray-400" : "text-muted"}
                                      style={{ fontSize: "0.7rem" }}
                                    >
                                      Cliquer sur le point pour ouvrir le détail complet de l&apos;alerte.
                                    </div>
                                  </div>
                                </div>
                              </Tooltip>
                            </Marker>
                          );
                        })}
                      </MapContainer>
                    </div>
                  )}

                  {alertesAvecCoordonnees.length > 0 && (
                    <div className="mt-3">
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        <span className={isDarkMode ? "text-gray-300 small" : "text-muted small"}>
                          <strong>Légende&nbsp;:</strong> couleur des points par priorité
                        </span>
                        <div className="d-flex flex-wrap gap-2">
                          {Object.entries(prioriteSummary)
                            .sort(([a, _], [b, __]) => Number(a) - Number(b))
                            .map(([id, info]) => {
                              const variant = getPrioriteBadgeVariant(Number(id), info.label);
                              return (
                                <div
                                  key={id}
                                  className="d-flex align-items-center small px-2 py-1 rounded-3 bg-light-subtle"
                                >
                                  <img
                                    src={`https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${getMarkerColorForPriorite(
                                      Number(id)
                                    )}.png`}
                                    alt={info.label}
                                    style={{
                                      width: "16px",
                                      height: "26px",
                                      marginRight: "6px",
                                    }}
                                  />
                                  <span
                                    className={`badge rounded-pill bg-${variant} d-flex align-items-center border-0`}
                                  >
                                    <span className="me-1">
                                      {info.label} (Priorité {id})
                                    </span>
                                    <span
                                      className={`badge ms-1 ${
                                        isDarkMode ? "bg-slate-900 text-gray-100" : "bg-white text-dark"
                                      }`}
                                      style={{ minWidth: 28, textAlign: "center" }}
                                    >
                                      {info.count}
                                    </span>
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Cartographie;
